/**
 * Shared DTOs — the wire-format contract between `apps/api` and `apps/web`.
 *
 * Each DTO is defined as a Zod schema; the TypeScript type is inferred from
 * the schema so there is exactly one source of truth. Use the schemas to:
 *   - validate request bodies on the API side (Fastify route schemas)
 *   - validate JSONB columns on read at the repository boundary
 *   - parse API responses defensively on the web side when needed
 *
 * Adding a new field: edit the schema once. Both apps inherit the type.
 */

import { z } from 'zod';

// ---------- enums ----------

export const ClassroomStatus = z.enum(['active', 'archived']);
export type ClassroomStatus = z.infer<typeof ClassroomStatus>;

export const SearchEngineDto = z.enum(['kiddle', 'google', 'bing']);
export type SearchEngineDto = z.infer<typeof SearchEngineDto>;

export const EventSourceDto = z.enum(['manual', 'school-pdf']);
export type EventSourceDto = z.infer<typeof EventSourceDto>;

// ---------- classroom ----------

export const ClassroomDto = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  schoolYear: z.string(),
  subject: z.string().nullable(),
  gradeLevel: z.string().nullable(),
  status: ClassroomStatus,
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ClassroomDto = z.infer<typeof ClassroomDto>;

export const CreateClassroomBody = z.object({
  name: z.string().min(1).max(120),
  schoolYear: z.string().min(1).max(20),
  subject: z.string().max(120).nullable().optional(),
  gradeLevel: z.string().max(20).nullable().optional(),
});
export type CreateClassroomBody = z.infer<typeof CreateClassroomBody>;

// ---------- preferences ----------

export const MmPagesEnabledDto = z.object({
  calendar: z.boolean(),
  'day-of-school': z.boolean(),
  number: z.boolean(),
  weather: z.boolean(),
  feelings: z.boolean(),
  question: z.boolean(),
  pledges: z.boolean(),
});
export type MmPagesEnabledDto = z.infer<typeof MmPagesEnabledDto>;

export const PreferencesDto = z.object({
  themeKey: z.string(),
  searchEngine: SearchEngineDto,
  showYesterdayTomorrow: z.boolean(),
  mmPagesEnabled: MmPagesEnabledDto,
  updatedAt: z.string(),
});
export type PreferencesDto = z.infer<typeof PreferencesDto>;

// ---------- me ----------

export const MeDto = z.object({
  teacher: z.object({
    id: z.string().uuid(),
    email: z.string(),
    displayName: z.string(),
    organizationId: z.string().uuid(),
    firstSeenAt: z.string(),
    lastLoginAt: z.string(),
  }),
  organization: z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    enabledThemeKeys: z.array(z.string()),
  }),
});
export type MeDto = z.infer<typeof MeDto>;

// ---------- classroom settings ----------

export const ClassroomSettingsDto = z.object({
  classroomId: z.string().uuid(),
  greeting: z.string(),
  message: z.string(),
  lastSelectedDay: z.number().int().nullable(),
  zipCode: z.string(),
  schoolCalUrl: z.string(),
  schoolCalTitle: z.string(),
  questionShowDaily: z.boolean(),
  questionShowWeekly: z.boolean(),
  questionDaily: z.string(),
  questionWeekly: z.string(),
  questionDailyAnswer: z.string(),
  questionWeeklyAnswer: z.string(),
  numberDrawingEnabled: z.boolean(),
  /** Which Home layout to render: 'k3' (mascot-forward) or 'g48' (dense 4–8). */
  homeVariant: z.enum(['k3', 'g48']),
  updatedAt: z.string(),
});
export type ClassroomSettingsDto = z.infer<typeof ClassroomSettingsDto>;

// ---------- planner: to-do + turn-in (4–8 home) ----------

/** A day's To-Do list. `date` is ISO YYYY-MM-DD; `lines` are ordered tasks. */
export const TodoDayDto = z.object({
  date: z.string(),
  lines: z.array(z.string()),
});
export type TodoDayDto = z.infer<typeof TodoDayDto>;

/** A Turn-In subject (defined once, ordered by `position`). */
export const TurnInSubjectDto = z.object({
  id: z.string().uuid(),
  name: z.string(),
  position: z.number().int(),
});
export type TurnInSubjectDto = z.infer<typeof TurnInSubjectDto>;

/** What's due for a subject on a given date. `date` is ISO YYYY-MM-DD. */
export const TurnInAssignmentDto = z.object({
  subjectId: z.string().uuid(),
  date: z.string(),
  assignment: z.string(),
});
export type TurnInAssignmentDto = z.infer<typeof TurnInAssignmentDto>;

// ---------- schedule ----------

export const ScheduleItemDto = z.object({
  id: z.string().uuid(),
  text: z.string(),
  /** Weekday indices, 0=Sun, 6=Sat. */
  days: z.array(z.number().int().min(0).max(6)).min(1).max(7),
  position: z.number().int(),
  createdAt: z.string(),
});
export type ScheduleItemDto = z.infer<typeof ScheduleItemDto>;

// ---------- calendar ----------

export const CalendarEventDto = z.object({
  id: z.string().uuid(),
  /** ISO YYYY-MM-DD. */
  eventDate: z.string(),
  title: z.string(),
  source: EventSourceDto,
  createdAt: z.string(),
});
export type CalendarEventDto = z.infer<typeof CalendarEventDto>;

// ---------- birthdays ----------

export const BirthdayDto = z.object({
  id: z.string().uuid(),
  name: z.string(),
  month: z.number().int().min(1).max(12),
  day: z.number().int().min(1).max(31),
  createdAt: z.string(),
});
export type BirthdayDto = z.infer<typeof BirthdayDto>;

// Per-day classroom content (message + question-of-the-day). On read this is
// the effective / carried-forward config for the date; setThisDay is false
// when the values were carried forward from an earlier day rather than set on
// that exact date.
export const ClassroomDayDto = z.object({
  classroomId: z.string().uuid(),
  dayDate: z.string(),
  message: z.string(),
  questionDaily: z.string(),
  questionDailyAnswer: z.string(),
  setThisDay: z.boolean(),
  updatedAt: z.string().nullable(),
});
export type ClassroomDayDto = z.infer<typeof ClassroomDayDto>;

// One recorded search from the day's activity log.
export const SearchEntryDto = z.object({
  id: z.string().uuid(),
  dayDate: z.string(),
  occurredAt: z.string(),
  kind: z.enum(['web', 'youtube']),
  engine: z.string(),
  query: z.string(),
});
export type SearchEntryDto = z.infer<typeof SearchEntryDto>;

// Aggregated "Day in review": one date's config + activity in a single payload.
// (Calendar events + birthdays are shown by DayDetail from its own props.)
export const DayReviewDto = z.object({
  date: z.string(),
  message: z.string(),
  questionDaily: z.string(),
  questionDailyAnswer: z.string(),
  setThisDay: z.boolean(),
  searches: z.array(SearchEntryDto),
  todo: z.array(z.string()),
  turnIn: z.array(
    z.object({ subjectId: z.string(), subjectName: z.string(), assignment: z.string() }),
  ),
  weather: z.object({ kind: z.string().nullable(), temperature: z.number().nullable() }).nullable(),
});
export type DayReviewDto = z.infer<typeof DayReviewDto>;

// ---------- classroom layout (sections + standalone pages) ----------

/** One section in a classroom's live layout (e.g. "Morning Meeting"). */
export const LayoutSectionDto = z.object({
  key: z.string(),
  name: z.string(),
  /** Enabled page keys, in nav order. */
  pages: z.array(z.string()),
});
export type LayoutSectionDto = z.infer<typeof LayoutSectionDto>;

/**
 * A classroom's customizable live layout: the ordered sections + standalone
 * class-level pages that sit between the pinned Home and Search bookends.
 * Pinned pages (home/search) are never part of this.
 */
export const ClassroomLayoutDto = z.object({
  classroomId: z.string().uuid(),
  sections: z.array(LayoutSectionDto),
  standalone: z.array(z.string()),
  updatedAt: z.string().nullable(),
});
export type ClassroomLayoutDto = z.infer<typeof ClassroomLayoutDto>;

export const UpdateClassroomLayoutBody = z.object({
  sections: z.array(LayoutSectionDto),
  standalone: z.array(z.string()),
});
export type UpdateClassroomLayoutBody = z.infer<typeof UpdateClassroomLayoutBody>;

// ---------- Links (standalone class-level page) ----------

export const LinkDto = z.object({
  id: z.string().uuid(),
  url: z.string(),
  title: z.string(),
  faviconUrl: z.string(),
  position: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type LinkDto = z.infer<typeof LinkDto>;

export const AddLinkBody = z.object({
  url: z.string().min(1),
  title: z.string().optional(),
});
export type AddLinkBody = z.infer<typeof AddLinkBody>;

export const ReorderLinksBody = z.object({
  ids: z.array(z.string().uuid()).min(1),
});
export type ReorderLinksBody = z.infer<typeof ReorderLinksBody>;

// ---------- YouTube playlist ----------

export const YoutubeVideoDto = z.object({
  id: z.string().uuid(),
  videoId: z.string(),
  title: z.string(),
  channelTitle: z.string(),
  thumbnailUrl: z.string(),
  sourceUrl: z.string(),
  position: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type YoutubeVideoDto = z.infer<typeof YoutubeVideoDto>;

export const YoutubeSearchResultDto = z.object({
  videoId: z.string(),
  title: z.string(),
  channelTitle: z.string(),
  thumbnailUrl: z.string(),
});
export type YoutubeSearchResultDto = z.infer<typeof YoutubeSearchResultDto>;

export const AddYoutubeVideoBody = z.object({
  videoUrl: z.string().optional(),
  videoId: z.string().optional(),
  title: z.string().optional(),
  channelTitle: z.string().optional(),
  thumbnailUrl: z.string().optional(),
});
export type AddYoutubeVideoBody = z.infer<typeof AddYoutubeVideoBody>;

export const ReorderYoutubeVideosBody = z.object({
  ids: z.array(z.string().uuid()).min(1),
});
export type ReorderYoutubeVideosBody = z.infer<typeof ReorderYoutubeVideosBody>;
